// src/Components/Fun.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Rating,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  Tooltip,
  InputAdornment,
  ImageList,
  ImageListItem
} from '@mui/material';
import {
  Add as AddIcon,
  Celebration as FunIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon,
  Movie as MovieIcon,
  Sports as SportsIcon,
  MusicNote as MusicIcon,
  Hiking as OutdoorIcon,
  Games as GamesIcon,
  LocalActivity as ActivityIcon,
  AddReaction as ReactionIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Place as PlaceIcon,
  PhotoCamera as PhotoIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  AccessTime as TimeIcon,
  Lock as PrivateIcon,
  Directions as DirectionsIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { notifyTaggedMembers } from '../services/notificationService';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import EmojiPicker from './EmojiPicker';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import logger from '../utils/logger';
import LocationPicker, { buildMapsUrl, buildDirectionsUrl } from './LocationPicker';

const Fun = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    rating: 3,
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: null,
    participants: '',
    cost: '',
    duration: '',
    wouldRecommend: true,
    tips: '',
    photos: [],
    contactPhone: '',
    contactWebsite: '',
    contactInstagram: '',
    contactFacebook: '',
    contactLinkedIn: '',
    contactTwitter: '',
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedActivityForComments, setSelectedActivityForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const db = getFirestore();
  const storage = getStorage();

  // Emoji reactions configuration
  const reactionTypes = [
    { emoji: '❤️', label: 'Love', value: 'love' },
    { emoji: '😂', label: 'Haha', value: 'haha' },
    { emoji: '😮', label: 'Wow', value: 'wow' },
    { emoji: '🎉', label: 'Celebrate', value: 'celebrate' },
    { emoji: '👍', label: 'Like', value: 'like' }
  ];

  const categories = [
    { value: 'general', label: 'General Fun', icon: <FunIcon />, color: '#2e7d32' },
    { value: 'dining', label: 'Dining Out', icon: <RestaurantIcon />, color: '#f57c00' },
    { value: 'movies', label: 'Movies & Shows', icon: <MovieIcon />, color: '#7b1fa2' },
    { value: 'sports', label: 'Sports & Fitness', icon: <SportsIcon />, color: '#0288d1' },
    { value: 'music', label: 'Music & Arts', icon: <MusicIcon />, color: '#c2185b' },
    { value: 'outdoor', label: 'Outdoor Activities', icon: <OutdoorIcon />, color: '#689f38' },
    { value: 'games', label: 'Games & Hobbies', icon: <GamesIcon />, color: '#6d4c41' }
  ];

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'funActivities'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const activitiesData = [];
        querySnapshot.forEach((doc) => {
          activitiesData.push({ id: doc.id, ...doc.data() });
        });
        setActivities(activitiesData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching activities:', error);
        setError('Failed to load activities');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Activity title is required');
      return;
    }

    try {
      const activityData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        rating: formData.rating,
        date: formData.date,
        time: formData.time || '',
        location: formData.location?.name || formData.location?.address || '',
        address: formData.location?.address || formData.location?.name || '',
        locationData: formData.location || null,
        participants: formData.participants,
        cost: formData.cost,
        duration: formData.duration,
        wouldRecommend: formData.wouldRecommend,
        tips: formData.tips,
        photos: formData.photos || [],
        contactPhone: formData.contactPhone || '',
        contactWebsite: formData.contactWebsite || '',
        contactInstagram: formData.contactInstagram || '',
        contactFacebook: formData.contactFacebook || '',
        contactLinkedIn: formData.contactLinkedIn || '',
        contactTwitter: formData.contactTwitter || '',
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        likes: {},
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      };

      if (editingActivity) {
        await updateDoc(doc(db, 'families', familyBoardId, 'funActivities', editingActivity.id), {
          ...activityData,
          likes: editingActivity.likes,
          updatedAt: serverTimestamp()
        });
        setSuccess('Activity updated');
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'funActivities'), activityData);
        setSuccess('Activity added');

        if (formData.taggedMembers && formData.taggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
            type: 'fun',
            id: docRef.id,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || 'Anonymous',
            actionUrl: '/fun'
          });
        }
      }

      setDialogOpen(false);
      setEditingActivity(null);
      resetFormData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving activity:', error);
      setError('Failed to save activity');
    }
  };

  const resetFormData = () => {
    setFormData({
      title: '', description: '', category: 'general', rating: 3,
      date: new Date().toISOString().split('T')[0], time: '', location: null,
      participants: '', cost: '', duration: '', wouldRecommend: true, tips: '',
      photos: [], contactPhone: '', contactWebsite: '', contactInstagram: '',
      contactFacebook: '', contactLinkedIn: '', contactTwitter: '',
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] }, taggedMembers: []
    });
  };

  // Handle photo upload for activity creation form
  const handleFormPhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length + formData.photos.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }

    setPhotoUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const storageRef = ref(storage, `families/${familyBoardId}/funActivities/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const photoUrls = await Promise.all(uploadPromises);
      setFormData({ ...formData, photos: [...formData.photos, ...photoUrls] });
    } catch (err) {
      logger.error('Error uploading photos:', err);
      setError('Failed to upload photos');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removeFormPhoto = (index) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'funActivities', id));
      setSuccess('Activity deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting activity:', error);
      setError('Failed to delete activity');
    }
  };

  // Handle emoji reactions
  const handleReaction = async (activityId, reactionType) => {
    try {
      const activity = activities.find(a => a.id === activityId);
      const reactions = activity.reactions || {};

      // Check if user already reacted with this type
      if (reactions[currentUser.uid]?.type === reactionType) {
        // Remove reaction
        delete reactions[currentUser.uid];
      } else {
        // Add or update reaction
        reactions[currentUser.uid] = {
          type: reactionType,
          name: currentUser.displayName || 'Anonymous',
          timestamp: new Date().toISOString()
        };
      }

      await updateDoc(doc(db, 'families', familyBoardId, 'funActivities', activityId), { reactions });
      setReactionAnchor(null);
    } catch (error) {
      logger.error('Error updating reaction:', error);
      setError('Failed to update reaction');
    }
  };

  // Get reaction counts grouped by type
  const getReactionCounts = (reactions) => {
    const counts = {};
    if (!reactions) return counts;

    Object.values(reactions).forEach(reaction => {
      counts[reaction.type] = (counts[reaction.type] || 0) + 1;
    });

    return counts;
  };

  // Get user's reaction if any
  const getUserReaction = (reactions) => {
    if (!reactions || !currentUser) return null;
    return reactions[currentUser.uid]?.type || null;
  };

  // Get total reaction count
  const getTotalReactions = (reactions) => {
    return reactions ? Object.keys(reactions).length : 0;
  };

  // Open reaction picker
  const openReactionPicker = (event, activityId) => {
    setReactionAnchor(event.currentTarget);
    setSelectedActivity(activityId);
  };

  // Subscribe to comments when dialog opens
  useEffect(() => {
    if (!selectedActivityForComments || !commentsDialogOpen || !familyBoardId) {
      setComments([]);
      return;
    }

    setLoadingComments(true);
    const q = query(
      collection(db, 'families', familyBoardId, 'funActivities', selectedActivityForComments.id, 'comments'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const commentsData = [];
        querySnapshot.forEach((doc) => {
          commentsData.push({ id: doc.id, ...doc.data() });
        });
        setComments(commentsData);
        setLoadingComments(false);
      },
      (error) => {
        logger.error('Error fetching comments:', error);
        setLoadingComments(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId, selectedActivityForComments, commentsDialogOpen]);

  // Open comments dialog
  const openCommentsDialog = (activity) => {
    setSelectedActivityForComments(activity);
    setCommentsDialogOpen(true);
    setNewComment('');
  };

  // Close comments dialog
  const closeCommentsDialog = () => {
    setCommentsDialogOpen(false);
    setSelectedActivityForComments(null);
    setComments([]);
    setNewComment('');
  };

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    try {
      await addDoc(collection(db, 'families', familyBoardId, 'funActivities', selectedActivityForComments.id, 'comments'), {
        content: newComment,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      });

      setNewComment('');
      setSuccess('Comment added');
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error adding comment:', error);
      setError('Failed to add comment');
    }
  };

  const openEditDialog = (activity) => {
    setEditingActivity(activity);
    setFormData({
      title: activity.title,
      description: activity.description || '',
      category: activity.category,
      rating: activity.rating || 3,
      date: activity.date,
      time: activity.time || '',
      location: (() => {
        if (activity.locationData?.name || activity.locationData?.address) return activity.locationData;
        if (activity.location || activity.address) {
          return { name: activity.location || '', address: activity.address || activity.location || '' };
        }
        return null;
      })(),
      participants: activity.participants || '',
      cost: activity.cost || '',
      duration: activity.duration || '',
      wouldRecommend: activity.wouldRecommend !== undefined ? activity.wouldRecommend : true,
      tips: activity.tips || '',
      photos: activity.photos || [],
      contactPhone: activity.contactPhone || '',
      contactWebsite: activity.contactWebsite || '',
      contactInstagram: activity.contactInstagram || '',
      contactFacebook: activity.contactFacebook || '',
      contactLinkedIn: activity.contactLinkedIn || '',
      contactTwitter: activity.contactTwitter || '',
      privacy: activity.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: activity.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingActivity(null);
    resetFormData();
    setDialogOpen(true);
  };

  const sanitizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('https://firebasestorage.googleapis.com') || url.startsWith('https://storage.googleapis.com')) return url;
    return url;
  };

  const getCategoryDetails = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Fun & Social Activities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share and remember your family's fun moments together
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Activity
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {activities.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <ActivityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No activities shared yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start documenting your family's fun times and create lasting memories!
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add First Activity
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {activities.map((activity) => {
            const categoryDetails = getCategoryDetails(activity.category);
            const reactionCounts = getReactionCounts(activity.reactions);
            const userReaction = getUserReaction(activity.reactions);
            const totalReactions = getTotalReactions(activity.reactions);

            return (
              <Grid item xs={12} md={6} lg={4} key={activity.id}>
                <Card sx={{
                  height: '100%',
                  '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
                  transition: 'all 0.3s',
                  border: '2px solid',
                  borderColor: categoryDetails.color
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip
                        icon={categoryDetails.icon}
                        label={categoryDetails.label}
                        size="small"
                        sx={{
                          bgcolor: categoryDetails.color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      {activity.authorId === currentUser.uid && (
                        <Box>
                          <IconButton size="small" onClick={() => openEditDialog(activity)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(activity.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: categoryDetails.color }}>
                      {activity.title}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                      <Rating value={activity.rating || 0} readOnly size="small" />
                      <Typography variant="caption" color="text.secondary">
                        ({activity.rating || 0}/5)
                      </Typography>
                    </Box>

                    {/* Privacy Badge */}
                    {activity.privacy?.visibility === 'private' && (
                      <Chip
                        icon={<PrivateIcon />}
                        label="Private"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mb: 1, width: 'fit-content' }}
                      />
                    )}

                    {/* Photos */}
                    {activity.photos && activity.photos.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <ImageList cols={Math.min(activity.photos.length, 3)} gap={4} sx={{ mt: 0, mb: 0 }}>
                          {activity.photos.slice(0, 3).map((url, idx) => (
                            <ImageListItem key={idx}>
                              <img src={sanitizeImageUrl(url)} alt={`Activity ${idx + 1}`} style={{ borderRadius: 6, maxHeight: 120, objectFit: 'cover' }} />
                            </ImageListItem>
                          ))}
                        </ImageList>
                        {activity.photos.length > 3 && (
                          <Typography variant="caption" color="text.secondary">+{activity.photos.length - 3} more photos</Typography>
                        )}
                      </Box>
                    )}

                    {activity.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {activity.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FunIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {format(parseISO(activity.date), 'MMM d, yyyy')}
                          {activity.time && ` at ${activity.time}`}
                        </Typography>
                      </Box>
                      {(activity.locationData || activity.location || activity.address) && (() => {
                        // Merge all location sources so truthy-but-empty locationData {} doesn't hide buttons
                        const loc = {
                          name: activity.locationData?.name || activity.location || null,
                          address: activity.locationData?.address || activity.address || activity.location || null,
                          lat: activity.locationData?.lat || null,
                          lng: activity.locationData?.lng || null,
                        };
                        const displayStr = loc.name || loc.address || '';
                        const mapsUrl = loc ? buildMapsUrl(loc) : null;
                        const dirsUrl = loc ? buildDirectionsUrl(loc) : null;
                        return (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PlaceIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">{displayStr}</Typography>
                            </Box>
                            {(mapsUrl || dirsUrl) && (
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, ml: 2.5 }}>
                                {mapsUrl && (
                                  <Button size="small" variant="text" sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                    onClick={(e) => { e.stopPropagation(); window.open(mapsUrl, '_blank'); }}>
                                    Map
                                  </Button>
                                )}
                                {dirsUrl && (
                                  <Button size="small" variant="text" startIcon={<DirectionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                                    sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                    onClick={(e) => { e.stopPropagation(); window.open(dirsUrl, '_blank'); }}>
                                    Directions
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                      {activity.participants && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {activity.participants}
                          </Typography>
                        </Box>
                      )}
                      {(activity.cost || activity.duration) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {activity.cost && (
                            <Typography variant="caption" color="text.secondary">
                              💰 {activity.cost}
                            </Typography>
                          )}
                          {activity.duration && (
                            <Typography variant="caption" color="text.secondary">
                              ⏱️ {activity.duration}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {activity.wouldRecommend !== undefined && (
                        <Chip
                          label={activity.wouldRecommend ? '✓ Recommended' : '⚠️ Not Recommended'}
                          size="small"
                          color={activity.wouldRecommend ? 'success' : 'default'}
                          sx={{ width: 'fit-content', fontSize: '0.7rem' }}
                        />
                      )}
                      {activity.tips && (
                        <Paper sx={{ p: 1, bgcolor: 'info.lighter', mt: 1 }}>
                          <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                            💡 {activity.tips}
                          </Typography>
                        </Paper>
                      )}
                    </Box>

                    {/* Social Contact Info Display */}
                    {(activity.contactPhone || activity.contactWebsite || activity.contactInstagram || activity.contactFacebook || activity.contactLinkedIn || activity.contactTwitter) && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                        {activity.contactPhone && (
                          <Chip icon={<PhoneIcon />} label={activity.contactPhone} size="small" variant="outlined" />
                        )}
                        {activity.contactWebsite && (
                          <Chip icon={<WebsiteIcon />} label={activity.contactWebsite} size="small" variant="outlined" clickable component="a" href={activity.contactWebsite.startsWith('http') ? activity.contactWebsite : `https://${activity.contactWebsite}`} target="_blank" rel="noopener noreferrer" />
                        )}
                        {activity.contactInstagram && (
                          <Chip icon={<InstagramIcon />} label={activity.contactInstagram} size="small" variant="outlined" />
                        )}
                        {activity.contactFacebook && (
                          <Chip icon={<FacebookIcon />} label={activity.contactFacebook} size="small" variant="outlined" />
                        )}
                        {activity.contactLinkedIn && (
                          <Chip icon={<LinkedInIcon />} label={activity.contactLinkedIn} size="small" variant="outlined" clickable component="a" href={activity.contactLinkedIn.startsWith('http') ? activity.contactLinkedIn : `https://${activity.contactLinkedIn}`} target="_blank" rel="noopener noreferrer" />
                        )}
                        {activity.contactTwitter && (
                          <Chip label={`X: ${activity.contactTwitter}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {activity.authorName}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Reaction Counts Display */}
                        {totalReactions > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {Object.entries(reactionCounts).map(([type, count]) => {
                              const reaction = reactionTypes.find(r => r.value === type);
                              return (
                                <Tooltip key={type} title={`${count} ${reaction?.label || type}`}>
                                  <Chip
                                    label={`${reaction?.emoji || '👍'} ${count}`}
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}

                        {/* Reaction Button */}
                        <Tooltip title="React">
                          <IconButton
                            size="small"
                            onClick={(e) => openReactionPicker(e, activity.id)}
                            sx={{
                              bgcolor: userReaction ? 'primary.light' : 'transparent',
                              '&:hover': { bgcolor: userReaction ? 'primary.main' : 'action.hover' }
                            }}
                          >
                            {userReaction ? (
                              <span style={{ fontSize: '1.2rem' }}>
                                {reactionTypes.find(r => r.value === userReaction)?.emoji || '👍'}
                              </span>
                            ) : (
                              <ReactionIcon />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Comment Button */}
                        <Tooltip title="Comments">
                          <IconButton
                            size="small"
                            onClick={() => openCommentsDialog(activity)}
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <CommentIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Reaction Picker Menu */}
      <Menu
        anchorEl={reactionAnchor}
        open={Boolean(reactionAnchor)}
        onClose={() => setReactionAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 0.5 }}>
          {reactionTypes.map((reaction) => (
            <Tooltip key={reaction.value} title={reaction.label}>
              <IconButton
                onClick={() => handleReaction(selectedActivity, reaction.value)}
                sx={{
                  fontSize: '1.5rem',
                  '&:hover': { transform: 'scale(1.2)', transition: 'transform 0.2s' }
                }}
              >
                {reaction.emoji}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Menu>

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialogOpen}
        onClose={closeCommentsDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        {selectedActivityForComments && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedActivityForComments.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Comments ({comments.length})
                </Typography>
              </Box>
              <IconButton onClick={closeCommentsDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Existing Comments */}
              <Box sx={{ mb: 2, maxHeight: '400px', overflowY: 'auto' }}>
                {loadingComments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : comments.length > 0 ? (
                  comments.map((comment) => (
                    <Paper key={comment.id} sx={{ p: 2, mb: 1.5, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {comment.authorName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {comment.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comment.createdAt && formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CommentIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No comments yet. Be the first to comment!
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Add Comment */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <EmojiPicker
                          onEmojiSelect={(emoji) => setNewComment(prev => prev + emoji)}
                          size="small"
                        />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  sx={{ minWidth: 100 }}
                >
                  Post
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Create/Edit Activity Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingActivity ? 'Edit Activity' : 'Add Fun Activity'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Activity Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., Movie night, Dinner at new restaurant"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Tell us about this fun activity... What's the plan? More info here."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, description: formData.description + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon}
                      <Typography>{cat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" gutterBottom>
                How fun was it? ({formData.rating}/5)
              </Typography>
              <Rating
                value={formData.rating}
                onChange={(event, newValue) => setFormData({ ...formData, rating: newValue || 0 })}
                size="large"
              />
            </Box>

            <Divider sx={{ my: 1 }}>
              <Chip label="When & Where" size="small" />
            </Divider>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><TimeIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
            </Grid>

            <LocationPicker
              value={formData.location}
              onChange={(loc) => setFormData({ ...formData, location: loc })}
              label="Place / Venue"
              placeholder="e.g., AMC Theater, Joe's Bistro, Central Park"
            />

            <TextField
              fullWidth
              label="Participants (Optional)"
              value={formData.participants}
              onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
              placeholder="Who's invited or participating? (e.g., Dad, Mom, Kids)"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cost (Optional)"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  placeholder="$50"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Duration (Optional)"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="2 hours"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Would you recommend this?</Typography>
                <Select
                  value={formData.wouldRecommend}
                  onChange={(e) => setFormData({ ...formData, wouldRecommend: e.target.value })}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </Box>
            </FormControl>

            <TextField
              fullWidth
              label="Tips & Recommendations (Optional)"
              value={formData.tips}
              onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
              multiline
              rows={2}
              placeholder="Any tips for others wanting to try this activity?"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, tips: formData.tips + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            {/* Photo Upload */}
            <Divider sx={{ my: 1 }}>
              <Chip label="Photos" size="small" />
            </Divider>

            <Box>
              <input
                type="file"
                ref={photoInputRef}
                onChange={handleFormPhotoUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              <Button
                startIcon={photoUploading ? <CircularProgress size={16} /> : <PhotoIcon />}
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploading || formData.photos.length >= 5}
                variant="outlined"
                size="small"
              >
                {photoUploading ? 'Uploading...' : `Add Photos (${formData.photos.length}/5)`}
              </Button>
              {formData.photos.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {formData.photos.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img
                        src={sanitizeImageUrl(url)}
                        alt={`Activity ${index + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeFormPhoto(index)}
                        sx={{
                          position: 'absolute', top: -8, right: -8,
                          bgcolor: 'background.paper', boxShadow: 1,
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Social Contact Info */}
            <Divider sx={{ my: 1 }}>
              <Chip label="Social & Contact Info" size="small" />
            </Divider>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder="(555) 123-4567"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Website"
                  value={formData.contactWebsite}
                  onChange={(e) => setFormData({ ...formData, contactWebsite: e.target.value })}
                  placeholder="https://example.com"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram"
                  value={formData.contactInstagram}
                  onChange={(e) => setFormData({ ...formData, contactInstagram: e.target.value })}
                  placeholder="@username"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Facebook"
                  value={formData.contactFacebook}
                  onChange={(e) => setFormData({ ...formData, contactFacebook: e.target.value })}
                  placeholder="facebook.com/page"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="LinkedIn"
                  value={formData.contactLinkedIn}
                  onChange={(e) => setFormData({ ...formData, contactLinkedIn: e.target.value })}
                  placeholder="linkedin.com/in/profile"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Twitter / X"
                  value={formData.contactTwitter}
                  onChange={(e) => setFormData({ ...formData, contactTwitter: e.target.value })}
                  placeholder="@handle"
                />
              </Grid>
            </Grid>

            {/* Privacy & Tagging */}
            <Divider sx={{ my: 1 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this activity?"
            />

            <Box sx={{ mt: 1 }}>
              <TagMembers
                familyCode={familyBoardId}
                value={formData.taggedMembers}
                onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
                label="Tag Family Members"
                helperText="Tagged members will receive a notification about this activity"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingActivity ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Fun;
